// 레이드 룩 고정 프레임 픽스처(C8-6, ?qa 전용). 정상 플레이 증거가 아니다 — 켜면 GameSim.qaOps에 센다.
// 문제(C8-5 #309 P1-3): raid-look-shots는 실입력 두뇌로 싸우며 조건이 맞는 첫 프레임을 찍어서, 회차마다 영웅-보스 거리(7~30m)·
// 장판 위치·용병 자리가 달라 같은 장면 점수가 ±0.5씩 흔들렸다. 이 픽스처는 장면마다 시드·틱·영웅/보스/용병 위치·경고·불 장판·
// 렌더 시계(영웅·세계 시각)를 고정한 상태를 매 틱 다시 써서, 몇 번을 찍어도 같은 프레임이 나오게 한다.
// 흐름: 마을이면 레이드 입장 명령 → 레이드 판이면 둥지(방 0)로 순간이동 → 비룡 등장이 시작되면 고정(이후 시뮬 진행을 멈춘다).
// 카메라는 사용자 조작 없는 기본 줌 그대로(연출 컷은 실시간 길이로 흘러 결정적이지 않으므로 이 픽스처 URL은 ?cine=off로 연다).
import { WYVERN, WyvernPattern } from '../shared/content/raid';
import { BossKind, BossState, HeroAct, Scene, TeleShape, type BossStateId } from '../shared/types';
import { MercAct } from './raid';
import type { GameSim } from './game';

export interface QaRaidFrame {
  /** 장면 이름(보고서·화면 감시용) */
  readonly name: string;
  readonly hero: readonly [x: number, z: number];
  readonly boss: { readonly x: number; readonly z: number; readonly state: BossStateId; readonly stateTick: number; readonly pattern: number; readonly phase2: boolean; readonly air: number; readonly hpPct: number };
  /** 용병 3명(탱커·궁수·사제) 자리 */
  readonly mercs: readonly (readonly [x: number, z: number])[];
  /** 원형 경고 [x, z, 진행 0..1] (반경·전체 틱은 화염 장판 값) */
  readonly tele?: readonly (readonly [x: number, z: number, progress: number])[];
  /** 불 장판 [x, z, 남은 비율 0..1] */
  readonly fires?: readonly (readonly [x: number, z: number, left: number])[];
  /** 2페이즈 전환 발광 솟음 0..1(렌더가 이 값을 그대로 쓴다) */
  readonly surge?: number;
  /** 렌더 시계 고정값(틱): 맥동·반복 클립 시각이 이것으로 정해진다 */
  readonly heroTime: number;
  readonly worldTime: number;
}

const P = WYVERN.pools;
/** 둥지 중심(raid 지도 방 0 영역 가운데)·비룡 착지점(방 bossAt) */
export const NEST = { x: 0, z: -46 } as const;
/**
 * 고정 장면 표. 영웅-보스 거리를 장면마다 정해 둔다(데스크톱 9~12m, 폰 9~10m — C8-5 r3~r5 실측 중앙값 근처).
 * 이름은 raid-look-shots 장면 기호와 같다(A·B·C·D·E·F·G). B는 터지는 순간(사건 VFX)이 아니라 장판이 깔린 직후다.
 */
export const RAID_FRAMES: Readonly<Record<string, QaRaidFrame>> = {
  'A-p1-telegraph': {
    name: 'A-p1-telegraph', hero: [0, -44], mercs: [[-1.6, -48.5], [6.5, -41], [-5.5, -40.5]],
    boss: { x: 0, z: -53, state: BossState.Cast, stateTick: Math.round(P.windup * 0.6), pattern: WyvernPattern.pools, phase2: false, air: 0, hpPct: 0.97 },
    tele: [[0, -44, 0.6], [6.5, -41, 0.6], [-5.5, -40.5, 0.6], [3.2, -47.5, 0.6]],
    heroTime: 6000, worldTime: 6000,
  },
  'B-p1-pools': {
    name: 'B-p1-pools', hero: [-2.2, -45.5], mercs: [[-1.6, -48.5], [7.5, -43], [-6.5, -41.5]],
    boss: { x: 0, z: -53, state: BossState.Sweep, stateTick: 4, pattern: WyvernPattern.pools, phase2: false, air: 0, hpPct: 0.96 },
    fires: [[0, -44, 0.98], [6.5, -41, 0.98], [-5.5, -40.5, 0.98], [3.2, -47.5, 0.98]],
    heroTime: 6060, worldTime: 6060,
  },
  'C-p2-edge': {
    name: 'C-p2-edge', hero: [-2, -47], mercs: [[-6.5, -53], [5, -42], [-1, -40]],
    boss: { x: -9, z: -57.5, state: BossState.Chase, stateTick: 30, pattern: -1, phase2: true, air: 0, hpPct: 0.45 },
    fires: [[3, -52, 0.6], [-4, -42.5, 0.35]],
    heroTime: 9000, worldTime: 9000,
  },
  'D-intro-a': {
    name: 'D-intro-a', hero: [0, -40], mercs: [[-1.4, -37.8], [1.4, -37.8], [0, -37]],
    boss: { x: 0, z: -54, state: BossState.Intro, stateTick: 60, pattern: -1, phase2: false, air: 0, hpPct: 1 },
    heroTime: 4000, worldTime: 4000,
  },
  // C8-7: 틱 80은 착지 충격 직후 웅크린 프레임(land 1.48초, 머리 4.8m)이라 위에서 머리가 안 보였다(블라인드 P1). 그 자세는 0.1초짜리
  // 충격 박자라 정지 컷의 대표로 맞지 않는다 → 발이 닿는 순간(틱 75 = 하강 끝, land 1.4초, 머리 9.5m·몸 펴짐)을 '착지' 장면으로 고른다
  'D-intro-b': {
    name: 'D-intro-b', hero: [0, -41], mercs: [[-1.4, -38.8], [1.4, -38.8], [0, -38]],
    boss: { x: 0, z: -54, state: BossState.Intro, stateTick: 75, pattern: -1, phase2: false, air: 0, hpPct: 1 },
    heroTime: 4015, worldTime: 4015,
  },
  'D-intro-c': {
    name: 'D-intro-c', hero: [0, -43], mercs: [[-1.4, -40.8], [1.4, -40.8], [0, -40]],
    boss: { x: 0, z: -54, state: BossState.Intro, stateTick: 115, pattern: -1, phase2: false, air: 0, hpPct: 1 },
    heroTime: 4055, worldTime: 4055,
  },
  'E-phone-p1': {
    name: 'E-phone-p1', hero: [-2, -48.5], mercs: [[-5, -53.5], [5, -44], [-6, -42]],
    boss: { x: -6, z: -57.5, state: BossState.Chase, stateTick: 20, pattern: -1, phase2: false, air: 0, hpPct: 0.9 },
    heroTime: 7000, worldTime: 7000,
  },
  // 보스가 영웅 오른쪽(스킬 단추 호 쪽)에 선 폰 장면 — C8-5 블라인드 P1 '머리가 우측 단추·전경 기둥에 걸림'의 최악 쪽
  'E2-phone-right': {
    name: 'E2-phone-right', hero: [-3, -47.5], mercs: [[1, -52], [-8, -43], [-6, -41]],
    boss: { x: 6, z: -55.5, state: BossState.Chase, stateTick: 20, pattern: -1, phase2: false, air: 0, hpPct: 0.88 },
    heroTime: 7200, worldTime: 7200,
  },
  'F-phone-p2': {
    name: 'F-phone-p2', hero: [3, -49.5], mercs: [[6.5, -55], [-4, -45], [8, -43]],
    boss: { x: 6, z: -58.5, state: BossState.Chase, stateTick: 20, pattern: -1, phase2: true, air: 0, hpPct: 0.47 },
    fires: [[-1, -52, 0.7], [9, -48, 0.4]],
    heroTime: 9500, worldTime: 9500,
  },
  'G-p2-surge': {
    name: 'G-p2-surge', hero: [0, -45], mercs: [[-2.5, -49.5], [6, -42], [-5.5, -41]],
    boss: { x: 0, z: -54, state: BossState.Chase, stateTick: 12, pattern: -1, phase2: true, air: 0, hpPct: 0.5 },
    surge: 1 - 0.2 / 1.4,
    heroTime: 8800, worldTime: 8800,
  },
};

/**
 * 고정 프레임 한 틱. true면 이 틱은 고정만 하고 시뮬을 진행하지 않는다(호출자가 stepInner를 건너뛴다).
 * false면 아직 준비 중(마을 → 레이드 입장, 둥지 입장 대기)이라 평소처럼 진행한다.
 */
export function pinQaRaidFrame(g: GameSim, f: QaRaidFrame, stage: { armed: number }): boolean {
  const flow = g.flow, r = g.raid.state, b = g.boss, h = g.hero;
  if (flow.scene === Scene.Village) {
    if (stage.armed === 0 && !flow.paused && flow.travelTo === 0) { g.command({ type: 'enterRaid', raid: 'raid_wyvern' }); stage.armed = 1; }
    return false;
  }
  if (flow.scene !== Scene.Dungeon || !r.active) return false;
  if (b.kind !== BossKind.Wyvern || b.state === BossState.Inactive) {
    // 둥지 입구 안쪽으로 한 번 옮기면 흐름이 방 입장 → 비룡 등장을 정상 경로로 시작한다
    if (stage.armed < 2) { g.flowController.qaGotoRoom(0); stage.armed = 2; }
    return false;
  }
  // 처음 고정하는 틱: 장면 번호를 올려 렌더가 입장·등장 사건의 남은 VFX(먼지 등)를 지우고 카메라를 바로 붙인다.
  // 그 VFX 나이는 마을에서 머문 시간에 따라 달라 프레임이 회차마다 달라진다
  if (stage.armed !== 3) flow.sceneSerial++;
  stage.armed = 3;
  flow.paused = false;
  h.x = h.prevX = f.hero[0]; h.z = h.prevZ = f.hero[1]; h.y = h.prevY = 0;
  h.facing = Math.atan2(f.boss.x - h.x, f.boss.z - h.z);
  h.act = HeroAct.Idle; h.actTick = 0; h.hp = h.maxHp; h.invulnerable = false;
  b.x = b.prevX = f.boss.x; b.z = b.prevZ = f.boss.z;
  b.facing = Math.atan2(h.x - b.x, h.z - b.z);
  b.state = f.boss.state; b.stateTick = f.boss.stateTick;
  b.phase2 = f.boss.phase2; b.air = f.boss.air; b.hp = b.maxHp * f.boss.hpPct;
  b.hitTick = 0xffff; b.invulnerable = false;
  r.phase = f.boss.phase2 ? 2 : 1; r.air = f.boss.air; r.pattern = f.boss.pattern; r.enraged = false; r.target = 0; r.tauntTicks = 0;
  for (let i = 0; i < r.mercs.length; i++) {
    const m = r.mercs[i], at = f.mercs[i] ?? [h.x + (i - 1) * 1.4, h.z + 2.2];
    m.x = m.prevX = at[0]; m.z = m.prevZ = at[1];
    m.facing = Math.atan2(b.x - m.x, b.z - m.z);
    m.act = MercAct.Idle; m.actTick = 0; m.down = 0; m.hp = m.maxHp; m.hurtTick = 0xffff; m.evading = false;
  }
  const fires = f.fires ?? [];
  for (let i = 0; i < r.fires.length; i++) {
    const p = r.fires[i], at = fires[i];
    p.active = !!at;
    if (!at) continue;
    p.x = at[0]; p.z = at[1]; p.radius = WYVERN.fire.radius; p.total = WYVERN.fire.ticks; p.ttl = Math.round(WYVERN.fire.ticks * at[2]); p.clock = 0;
  }
  const tele = f.tele ?? [];
  for (let i = 0; i < g.telegraphs.length; i++) {
    const t = g.telegraphs[i], at = tele[i];
    t.active = !!at;
    if (!at) continue;
    t.shape = TeleShape.Circle; t.x = at[0]; t.z = at[1]; t.radius = P.radius; t.angle = 0; t.width = 0; t.total = P.windup; t.tick = P.windup * at[2];
  }
  return true;
}
